import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useScrollAnimation, useScrollAnimationMultiple } from "@/hooks/useScrollAnimation";
import type React from "react";

const faqsData = [
  // Q1: 名前を変えないと意味がありませんか？
  {
    qJa: "名前を変えないと意味がありませんか？",
    qEn: "Does it work without changing my name?",
    qIt: "Funziona senza cambiare il mio nome?",
    qFr: "Cela fonctionne-t-il sans changer mon nom ?",
    qDe: "Funktioniert es, ohne meinen Namen zu \u00e4ndern?",
    qEs: "\u00bfFunciona sin cambiar mi nombre?",
    qRu: "Работает ли это без смены имени?",
    qZh: "不改名字也有效果吗？",
    qKo: "이름을 바꾸지 않으면 의미가 없나요?",
    qPt: "Funciona sem mudar meu nome?",
    qAr: "هل يعمل بدون تغيير اسمي؟",
    qHi: "क्या नाम बदले बिना भी काम करता है?",
    qTh: "มันได้ผลโดยไม่ต้องเปลี่ยนชื่อหรือ?",
    qVi: "Có hiệu quả mà không cần đổi tên không?",
    qId: "Apakah berhasil tanpa mengganti nama?",
    qTl: "Gumagana ba ito nang hindi pinapalitan ang pangalan?",
    qNe: "नाम नबदलीकन पनि काम गर्छ?",
    qZhTW: "不改名字也有效果嗎？",
    aJa: "いいえ、改名はしませんし、必要ありません。今のお名前に、そっと「てんてん（濁点）」を添えて整えます。",
    aEn: "No, you do not change your name and it is not necessary. We gently add ten-ten (voiced marks) to your current name to harmonize it.",
    aIt: "No, non si cambia il nome e non è necessario. Aggiungiamo delicatamente i ten-ten al tuo nome attuale per armonizzarlo.",
    aFr: "Non, vous ne changez pas votre nom et ce n'est pas nécessaire. Nous ajoutons doucement des ten-ten à votre nom actuel pour l'harmoniser.",
    aDe: "Nein, Sie \u00e4ndern Ihren Namen nicht und das ist auch nicht n\u00f6tig. Wir f\u00fcgen Ihrem aktuellen Namen sanft Ten-ten hinzu, um ihn zu harmonisieren.",
    aEs: "No, no cambia su nombre y no es necesario. Añadimos suavemente ten-ten a su nombre actual para armonizarlo.",
    aRu: "Нет, вы не меняете имя, и это не нужно. Мы мягко добавляем тен-тен к вашему текущему имени, чтобы гармонизировать его.",
    aZh: "不需要改名，也不必这样做。我们温柔地在您现有的名字上添加浊点（てんてん）来调和它。",
    aKo: "아니요, 개명은 하지 않으며 필요하지도 않습니다. 현재 이름에 조용히 '탁점(てんてん)'을 더해 조화롭게 합니다.",
    aPt: "Não, você não muda seu nome e não é necessário. Adicionamos suavemente ten-ten ao seu nome atual para harmonizá-lo.",
    aAr: "لا، لا تغير اسمك وليس ضرورياً. نضيف بلطف علامات التنتن إلى اسمك الحالي لتنسيقه.",
    aHi: "नहीं, आप अपना नाम नहीं बदलते और यह जरूरी भी नहीं है। हम आपके वर्तमान नाम में सौम्यता से तेंतेन जोड़कर उसे संतुलित करते हैं।",
    aTh: "ไม่ คุณไม่ต้องเปลี่ยนชื่อและไม่จำเป็นต้องทำ เราเพิ่มจุดเทนเทนลงในชื่อปัจจุบันของคุณอย่างอ่อนโยนเพื่อปรับสมดุล",
    aVi: "Không, bạn không cần đổi tên và cũng không cần thiết. Chúng tôi nhẹ nhàng thêm dấu tenten vào tên hiện tại của bạn để điều hòa.",
    aId: "Tidak, Anda tidak perlu mengganti nama dan itu tidak diperlukan. Kami dengan lembut menambahkan ten-ten pada nama Anda saat ini untuk menyelaraskannya.",
    aTl: "Hindi, hindi mo kailangang palitan ang pangalan at hindi ito kinakailangan. Malambing naming idinaragdag ang ten-ten sa iyong kasalukuyang pangalan upang pagkaayon-ayunin ito.",
    aNe: "होइन, नाम परिवर्तन गर्नु पर्दैन र आवश्यक पनि छैन। हामी तपाईंको हालको नाममा सौम्यतापूर्वक तेन-तेन थपेर यसलाई सुसंगत बनाउँछौं।",
    aZhTW: "不需要改名，也不必這樣做。我們溫柔地在您現有的名字上添加濁點（てんてん）來調和它。",
  },
  // Q2: てんてんをつけるだけで変化があるんですか？
  {
    qJa: "「てんてん」をつけるだけで、変化があるんですか？",
    qEn: "Can just adding ten-ten really make a difference?",
    qIt: "Aggiungere solo i ten-ten può davvero fare la differenza?",
    qFr: "Ajouter simplement des ten-ten peut-il vraiment faire une différence ?",
    qDe: "Kann das Hinzuf\u00fcgen von Ten-ten wirklich einen Unterschied machen?",
    qEs: "\u00bfA\u00f1adir solo ten-ten puede realmente marcar la diferencia?",
    qRu: "Может ли простое добавление тен-тен действительно изменить что-то?",
    qZh: "仅仅添加浊点真的会有变化吗？",
    qKo: "탁점을 더하는 것만으로 변화가 있나요?",
    qPt: "Apenas adicionar ten-ten pode realmente fazer diferença?",
    qAr: "هل مجرد إضافة التنتن يمكن أن يحدث فرقاً حقيقياً؟",
    qHi: "क्या सिर्फ तेंतेन जोड़ने से वाकई फर्क पड़ सकता है?",
    qTh: "แค่เพิ่มเทนเทนจะทำให้เกิดความแตกต่างได้จริงหรือ?",
    qVi: "Chỉ thêm tenten thôi có thực sự tạo ra sự khác biệt không?",
    qId: "Apakah hanya menambahkan tenten benar-benar bisa membuat perbedaan?",
    qTl: "Maaari bang gumawa ng pagkakaiba ang pagdaragdag lamang ng tenten?",
    qNe: "टेन्टेन मात्र थप्दा साँच्चै फरक पर्छ?",
    qZhTW: "僅僅添加濁點真的會有變化嗎？",
    aJa: "急激な変化というより、心や運の流れがやさしく整うイメージです。",
    aEn: "Rather than dramatic change, think of it as gently harmonizing the flow of your heart and fortune.",
    aIt: "Piuttosto che un cambiamento drastico, immaginalo come un'armonizzazione delicata del flusso del tuo cuore e della tua fortuna.",
    aFr: "Plutôt qu'un changement dramatique, imaginez-le comme une harmonisation douce du flux de votre cœur et de votre fortune.",
    aDe: "Eher als dramatische Ver\u00e4nderung stellen Sie es sich als sanfte Harmonisierung des Flusses Ihres Herzens und Ihrer Gl\u00fcckslage vor.",
    aEs: "M\u00e1s que un cambio dram\u00e1tico, pi\u00e9nselo como una armonizaci\u00f3n suave del flujo de su coraz\u00f3n y su fortuna.",
    aRu: "Скорее, чем драматические перемены, представьте это как мягкую гармонизацию потока вашего сердца и судьбы.",
    aZh: "与其说是巨大变化，不如说是心境和运势的流动得到温柔调整的感觉。",
    aKo: "급격한 변화라기보다는, 마음과 운의 흐름이 부드럽게 정돈되는 이미지입니다.",
    aPt: "Mais do que uma mudança dramática, pense nisso como uma harmonização suave do fluxo do seu coração e da sua fortuna.",
    aAr: "بدلاً من التغيير الجذري، فكر في الأمر كتنسيق لطيف لتدفق قلبك وحظك.",
    aHi: "नाटकीय बदलाव की बजाय, इसे अपने दिल और भाग्य के प्रवाह को सौम्यता से संतुलित करने के रूप में सोचें।",
    aTh: "มากกว่าการเปลี่ยนแปลงอย่างรุนแรง ลองนึกถึงมันว่าเป็นการปรับสมดุลอย่างอ่อนโยนของกระแสหัวใจและโชคชะตาของคุณ",
    aVi: "Hơn là thay đổi kịch tính, hãy nghĩ đó là sự điều hòa nhẹ nhàng dòng chảy của trái tim và vận may của bạn.",
    aId: "Daripada perubahan dramatis, bayangkan ini sebagai harmonisasi lembut aliran hati dan keberuntungan Anda.",
    aTl: "Sa halip na dramatikong pagbabago, isipin ito bilang malambot na pag-aayos ng daloy ng iyong puso at kapalaran.",
    aNe: "नाटकीय परिवर्तनभन्दा, यसलाई तपाईंको हृदय र भाग्यको प्रवाहलाई सौम्यतापूर्वक सुसंगत बनाउने रूपमा सोच्नुहोस्।",
    aZhTW: "與其說是巨大變化，不如說是心境和運勢的流動得到溫柔調整的感覺。",
  },
  // Q3: オンラインで完結しますか？
  {
    qJa: "オンラインで完結しますか？",
    qEn: "Is everything done online?",
    qIt: "Tutto si svolge online?",
    qFr: "Tout se fait-il en ligne ?",
    qDe: "Wird alles online abgewickelt?",
    qEs: "\u00bfTodo se hace en l\u00ednea?",
    qRu: "Всё происходит онлайн?",
    qZh: "可以全程线上完成吗？",
    qKo: "온라인으로 모두 완결되나요?",
    qPt: "Tudo é feito online?",
    qAr: "هل يتم كل شيء عبر الإنترنت؟",
    qHi: "क्या सब कुछ ऑनलाइन हो जाता है?",
    qTh: "ทุกอย่างทำออนไลน์ได้หรือ?",
    qVi: "Có thể hoàn tất mọi thứ trực tuyến không?",
    qId: "Apakah semuanya bisa dilakukan secara online?",
    qTl: "Lahat ba ay magagawa online?",
    qNe: "सबै कुरा अनलाइन हुन्छ?",
    qZhTW: "可以全程線上完成嗎？",
    aJa: "はい。お申し込みから受け取りまで、すべてオンラインで完結します。",
    aEn: "Yes. Everything from application to delivery is completed online.",
    aIt: "Sì. Tutto, dalla richiesta alla consegna, viene completato online.",
    aFr: "Oui. Tout, de la demande à la livraison, est effectué en ligne.",
    aDe: "Ja. Alles von der Anfrage bis zur Lieferung wird online abgewickelt.",
    aEs: "S\u00ed. Todo, desde la solicitud hasta la entrega, se completa en l\u00ednea.",
    aRu: "Да. Всё — от заявки до доставки — выполняется онлайн.",
    aZh: "是的。从申请到接收结果，全程线上完成。",
    aKo: "네. 신청부터 수령까지 모두 온라인으로 완결됩니다.",
    aPt: "Sim. Tudo, desde a inscrição até a entrega, é concluído online.",
    aAr: "نعم. كل شيء من التقديم حتى التسليم يتم عبر الإنترنت.",
    aHi: "हाँ। आवेदन से लेकर डिलीवरी तक सब कुछ ऑनलाइन पूरा होता है।",
    aTh: "ใช่ ทุกอย่างตั้งแต่การสมัครจนถึงการส่งมอบเสร็จสิ้นทางออนไลน์",
    aVi: "Có. Mọi thứ từ đăng ký đến nhận kết quả đều được thực hiện trực tuyến.",
    aId: "Ya. Semua mulai dari pendaftaran hingga pengiriman diselesaikan secara online.",
    aTl: "Oo. Lahat mula sa pagpaparehistro hanggang sa paghahatid ay nakumpleto online.",
    aNe: "हो। दर्तादेखि डेलिभरीसम्म सबै कुरा अनलाइन पूरा हुन्छ।",
    aZhTW: "是的。從申請到接收結果，全程線上完成。",
  },
  // Q4: 実際に沖縄へ行く必要はありますか？
  {
    qJa: "実際に沖縄へ行く必要はありますか？",
    qEn: "Do I need to travel to Okinawa?",
    qIt: "Devo recarmi a Okinawa?",
    qFr: "Dois-je me rendre à Okinawa ?",
    qDe: "Muss ich nach Okinawa reisen?",
    qEs: "\u00bfNecesito viajar a Okinawa?",
    qRu: "Нужно ли мне ехать на Окинаву?",
    qZh: "需要亲自去冲绳吗？",
    qKo: "실제로 오키나와에 가야 하나요?",
    qPt: "Preciso viajar para Okinawa?",
    qAr: "هل أحتاج إلى السفر إلى أوكيناوا؟",
    qHi: "क्या मुझे ओकिनावा जाना होगा?",
    qTh: "ฉันต้องเดินทางไปโอกินาวาหรือ?",
    qVi: "Tôi có cần đến Okinawa không?",
    qId: "Apakah saya perlu pergi ke Okinawa?",
    qTl: "Kailangan ko bang pumunta sa Okinawa?",
    qNe: "के मलाई ओकिनावा जानु पर्छ?",
    qZhTW: "需要親自去沖繩嗎？",
    aJa: "ありません。私が毎月、直接沖縄の島へ伺い、先生にお伝えします。",
    aEn: "No. I visit the island in Okinawa directly every month and convey your name to the master.",
    aIt: "No. Ogni mese vado direttamente sull'isola di Okinawa e trasmetto il tuo nome al maestro.",
    aFr: "Non. Je me rends directement sur l'île d'Okinawa chaque mois et transmets votre nom au maître.",
    aDe: "Nein. Ich besuche jeden Monat direkt die Insel in Okinawa und \u00fcberbringe Ihren Namen dem Meister.",
    aEs: "No. Visito la isla de Okinawa directamente cada mes y transmito su nombre al maestro.",
    aRu: "Нет. Я каждый месяц лично посещаю остров на Окинаве и передаю ваше имя мастеру.",
    aZh: "不需要。我每月亲自前往冲绳的岛屿，代您将名字转达给先生。",
    aKo: "없습니다. 제가 매월 직접 오키나와 섬을 방문하여 선생님께 전달합니다.",
    aPt: "Não. Visito a ilha em Okinawa diretamente todo mês e transmito seu nome ao mestre.",
    aAr: "لا. أزور الجزيرة في أوكيناوا مباشرة كل شهر وأنقل اسمك إلى المعلم.",
    aHi: "नहीं। मैं हर महीने सीधे ओकिनावा के द्वीप पर जाता हूं और आपका नाम गुरुजी को पहुंचाता हूं।",
    aTh: "ไม่ ฉันไปเยี่ยมเกาะในโอกินาวาโดยตรงทุกเดือนและส่งชื่อของคุณให้กับอาจารย์",
    aVi: "Không. Tôi trực tiếp đến thăm hòn đảo ở Okinawa mỗi tháng và chuyển tên của bạn đến thầy.",
    aId: "Tidak. Saya mengunjungi pulau di Okinawa secara langsung setiap bulan dan menyampaikan nama Anda kepada sang master.",
    aTl: "Hindi. Direkta akong bumibisita sa isla sa Okinawa bawat buwan at ipinaabot ang iyong pangalan sa guro.",
    aNe: "छैन। म हरेक महिना सिधै ओकिनावाको टापुमा जान्छु र तपाईंको नाम गुरुजीलाई पुर्‍याउँछु।",
    aZhTW: "不需要。我每月親自前往沖繩的島嶼，代您將名字轉達給先生。",
  },
  // Q5: 料金以外に追加費用はありますか？
  {
    qJa: "料金以外に追加費用はありますか？",
    qEn: "Are there any additional fees beyond the listed price?",
    qIt: "Ci sono costi aggiuntivi oltre al prezzo indicato?",
    qFr: "Y a-t-il des frais supplémentaires au-delà du prix indiqué ?",
    qDe: "Gibt es zus\u00e4tzliche Geb\u00fchren \u00fcber den angegebenen Preis hinaus?",
    qEs: "\u00bfHay tarifas adicionales m\u00e1s all\u00e1 del precio indicado?",
    qRu: "Есть ли дополнительные сборы сверх указанной цены?",
    qZh: "除了标价之外还有额外费用吗？",
    qKo: "요금 외에 추가 비용이 있나요?",
    qPt: "Há taxas adicionais além do preço indicado?",
    qAr: "هل توجد رسوم إضافية بخلاف السعر المدرج؟",
    qHi: "सूचीबद्ध मूल्य के अलावा कोई अतिरिक्त शुल्क है?",
    qTh: "มีค่าธรรมเนียมเพิ่มเติมนอกเหนือจากราคาที่ระบุหรือ?",
    qVi: "Có phí phát sinh nào ngoài giá niêm yết không?",
    qId: "Apakah ada biaya tambahan di luar harga yang tercantum?",
    qTl: "Mayroon bang karagdagang bayad bukod sa nakalista na presyo?",
    qNe: "सूचीबद्ध मूल्यभन्दा बाहेक कुनै अतिरिक्त शुल्क छ?",
    qZhTW: "除了標價之外還有額外費用嗎？",
    aJa: "いいえ、追加費用はありません。鑑定料・代行費・郵送費も含まれています。",
    aEn: "No, there are no additional fees. Reading fee, proxy fee, and mailing fee are all included.",
    aIt: "No, non ci sono costi aggiuntivi. La tariffa di lettura, il compenso per la rappresentanza e le spese di spedizione sono tutti inclusi.",
    aFr: "Non, il n'y a pas de frais supplémentaires. Les frais de lecture, de représentation et d'envoi sont tous inclus.",
    aDe: "Nein, es gibt keine zus\u00e4tzlichen Geb\u00fchren. Lesegeb\u00fchr, Vertretungsgeb\u00fchr und Versandkosten sind alle enthalten.",
    aEs: "No, no hay tarifas adicionales. La tarifa de lectura, la tarifa de representaci\u00f3n y los gastos de env\u00edo est\u00e1n todos incluidos.",
    aRu: "Нет, дополнительных сборов нет. Плата за чтение, представительский сбор и почтовые расходы — всё включено.",
    aZh: "没有额外费用。鉴定费、代理费和邮寄费均已包含在内。",
    aKo: "아니요, 추가 비용은 없습니다. 감정료·대행비·우편비 모두 포함되어 있습니다.",
    aPt: "Não, não há taxas adicionais. A taxa de leitura, a taxa de representação e a taxa de envio estão todas incluídas.",
    aAr: "لا، لا توجد رسوم إضافية. رسوم القراءة ورسوم الوكالة ورسوم البريد كلها مشمولة.",
    aHi: "नहीं, कोई अतिरिक्त शुल्क नहीं है। पठन शुल्क, प्रतिनिधित्व शुल्क और डाक शुल्क सभी शामिल हैं।",
    aTh: "ไม่ ไม่มีค่าธรรมเนียมเพิ่มเติม ค่าอ่าน ค่าตัวแทน และค่าไปรษณีย์รวมอยู่ทั้งหมด",
    aVi: "Không, không có phí phát sinh. Phí xem, phí đại diện và phí bưu điện đều đã bao gồm.",
    aId: "Tidak, tidak ada biaya tambahan. Biaya konsultasi, biaya perwakilan, dan biaya pengiriman semuanya sudah termasuk.",
    aTl: "Hindi, walang karagdagang bayad. Ang bayad sa pagbabasa, bayad sa kinatawan, at bayad sa koreo ay kasama na lahat.",
    aNe: "छैन, कुनै अतिरिक्त शुल्क छैन। पठन शुल्क, प्रतिनिधित्व शुल्क र हुलाक शुल्क सबै समावेश छन्।",
    aZhTW: "沒有額外費用。鑑定費、代理費和郵寄費均已包含在內。",
  },
  // Q6: 使い方が難しくありませんか？
  {
    qJa: "使い方が難しくありませんか？",
    qEn: "Is it difficult to use?",
    qIt: "È difficile da usare?",
    qFr: "Est-ce difficile à utiliser ?",
    qDe: "Ist es schwierig zu nutzen?",
    qEs: "\u00bfEs dif\u00edcil de usar?",
    qRu: "Это сложно использовать?",
    qZh: "使用起来难吗？",
    qKo: "사용하기 어렵지 않나요?",
    qPt: "É difícil de usar?",
    qAr: "هل من الصعب استخدامه؟",
    qHi: "क्या इसका उपयोग करना मुश्किल है?",
    qTh: "ใช้งานยากหรือเปล่า?",
    qVi: "Có khó sử dụng không?",
    qId: "Apakah sulit untuk digunakan?",
    qTl: "Mahirap ba itong gamitin?",
    qNe: "प्रयोग गर्न गाह्रो छ?",
    qZhTW: "使用起來難嗎？",
    aJa: "難しくありません。毎日の生活で、活用するだけで大丈夫です。",
    aEn: "Not at all. Simply being aware of it in your daily life is enough.",
    aIt: "Per niente. È sufficiente utilizzarlo nella vita quotidiana.",
    aFr: "Pas du tout. Il suffit d'en être conscient dans votre vie quotidienne.",
    aDe: "\u00dcberhaupt nicht. Es gen\u00fcgt, sich im Alltag daran zu erinnern.",
    aEs: "Para nada. Con simplemente tenerlo presente en su vida diaria es suficiente.",
    aRu: "Совсем нет. Достаточно просто помнить об этом в повседневной жизни.",
    aZh: "不难。只需在日常生活中留意即可。",
    aKo: "어렵지 않습니다. 일상생활에서 활용하기만 하면 됩니다.",
    aPt: "De jeito nenhum. Simplesmente estar ciente disso em sua vida diária é suficiente.",
    aAr: "ليس على الإطلاق. مجرد الوعي به في حياتك اليومية يكفي.",
    aHi: "बिल्कुल नहीं। बस अपने दैनिक जीवन में इसके प्रति जागरूक रहना पर्याप्त है।",
    aTh: "ไม่เลย แค่ตระหนักถึงมันในชีวิตประจำวันก็เพียงพอแล้ว",
    aVi: "Không hề. Chỉ cần ý thức về nó trong cuộc sống hàng ngày là đủ.",
    aId: "Sama sekali tidak. Cukup dengan menyadarinya dalam kehidupan sehari-hari Anda.",
    aTl: "Hindi talaga. Ang simpleng kamalayan nito sa iyong pang-araw-araw na buhay ay sapat na.",
    aNe: "हैन। दैनिक जीवनमा यसप्रति सचेत रहनु मात्र पर्याप्त छ।",
    aZhTW: "不難。只需在日常生活中留意即可。",
  },
  // Q7: 振込後にキャンセルできますか？
  {
    qJa: "振込後にキャンセルできますか？",
    qEn: "Can I cancel after transferring payment?",
    qIt: "Posso annullare dopo aver effettuato il pagamento?",
    qFr: "Puis-je annuler après avoir effectué le virement ?",
    qDe: "Kann ich nach der \u00dcberweisung stornieren?",
    qEs: "\u00bfPuedo cancelar despu\u00e9s de realizar el pago?",
    qRu: "Могу ли я отменить после перевода оплаты?",
    qZh: "转账后可以取消吗？",
    qKo: "이체 후 취소할 수 있나요?",
    qPt: "Posso cancelar após a transferência do pagamento?",
    qAr: "هل يمكنني الإلغاء بعد تحويل الدفع؟",
    qHi: "क्या भुगतान हस्तांतरण के बाद रद्द कर सकते हैं?",
    qTh: "ยกเลิกได้หลังโอนเงินหรือ?",
    qVi: "Có thể hủy sau khi chuyển khoản không?",
    qId: "Bisakah saya membatalkan setelah melakukan transfer pembayaran?",
    qTl: "Maaari ba akong mag-cancel pagkatapos ng paglilipat ng bayad?",
    qNe: "भुक्तानी ट्रान्सफर गरेपछि रद्द गर्न सकिन्छ?",
    qZhTW: "轉帳後可以取消嗎？",
    aJa: "鑑定開始前であればキャンセル可能です。開始後は対応できませんのでご了承ください。",
    aEn: "Cancellation is possible before the reading begins. Please note that cancellations cannot be accepted after the reading has started.",
    aIt: "La cancellazione è possibile prima che la lettura inizi. Si prega di notare che le cancellazioni non possono essere accettate dopo l'inizio della lettura.",
    aFr: "L'annulation est possible avant le début de la lecture. Veuillez noter que les annulations ne peuvent pas être acceptées après le début de la lecture.",
    aDe: "Eine Stornierung ist vor Beginn der Lesung m\u00f6glich. Bitte beachten Sie, dass nach Beginn der Lesung keine Stornierungen mehr akzeptiert werden k\u00f6nnen.",
    aEs: "La cancelaci\u00f3n es posible antes de que comience la lectura. Tenga en cuenta que no se pueden aceptar cancelaciones una vez iniciada la lectura.",
    aRu: "Отмена возможна до начала чтения. Обратите внимание, что после начала чтения отмены не принимаются.",
    aZh: "鉴定开始前可以取消。请注意，鉴定开始后无法受理取消。",
    aKo: "감정 시작 전이라면 취소 가능합니다. 시작 후에는 대응할 수 없으니 양해 부탁드립니다.",
    aPt: "O cancelamento é possível antes do início da leitura. Observe que cancelamentos não podem ser aceitos após o início da leitura.",
    aAr: "الإلغاء ممكن قبل بدء القراءة. يرجى ملاحظة أنه لا يمكن قبول الإلغاءات بعد بدء القراءة.",
    aHi: "पठन शुरू होने से पहले रद्द करना संभव है। कृपया ध्यान दें कि पठन शुरू होने के बाद रद्दीकरण स्वीकार नहीं किया जा सकता।",
    aTh: "ยกเลิกได้ก่อนเริ่มการอ่าน โปรดทราบว่าไม่สามารถรับการยกเลิกได้หลังจากเริ่มการอ่านแล้ว",
    aVi: "Có thể hủy trước khi bắt đầu xem. Xin lưu ý rằng không thể hủy sau khi đã bắt đầu xem.",
    aId: "Pembatalan dimungkinkan sebelum konsultasi dimulai. Harap diperhatikan bahwa pembatalan tidak dapat diterima setelah konsultasi dimulai.",
    aTl: "Posible ang pagkansela bago magsimula ang konsultasyon. Pakitandaan na hindi maaaring tanggapin ang mga pagkansela pagkatapos magsimula ang konsultasyon.",
    aNe: "पठन सुरु हुनुअघि रद्द गर्न सम्भव छ। कृपया ध्यान दिनुहोस् कि पठन सुरु भएपछि रद्दीकरण स्वीकार गर्न सकिँदैन।",
    aZhTW: "鑑定開始前可以取消。請注意，鑑定開始後無法受理取消。",
  },
  // Q8: どんな名前でも見てもらえますか？
  {
    qJa: "どんな名前でも見てもらえますか？",
    qEn: "Can any name be read?",
    qIt: "Si può leggere qualsiasi nome?",
    qFr: "N'importe quel nom peut-il être lu ?",
    qDe: "Kann jeder Name gelesen werden?",
    qEs: "\u00bfSe puede leer cualquier nombre?",
    qRu: "Можно ли прочитать любое имя?",
    qZh: "任何名字都可以鉴定吗？",
    qKo: "어떤 이름이든 봐주실 수 있나요?",
    qPt: "Qualquer nome pode ser lido?",
    qAr: "هل يمكن قراءة أي اسم؟",
    qHi: "क्या कोई भी नाम पढ़ा जा सकता है?",
    qTh: "อ่านชื่อใดก็ได้หรือ?",
    qVi: "Có thể xem bất kỳ tên nào không?",
    qId: "Apakah nama apa pun bisa dikonsultasikan?",
    qTl: "Maaari bang ikonsulta ang anumang pangalan?",
    qNe: "कुनै पनि नाम हेर्न सकिन्छ?",
    qZhTW: "任何名字都可以鑑定嗎？",
    aJa: "はい、可能です。年齢に応じたメニューがあります。",
    aEn: "Yes. It is possible. We have menus tailored to different age groups.",
    aIt: "Sì, è possibile. Abbiamo menu adatti a diverse fasce d'età.",
    aFr: "Oui, c'est possible. Nous avons des menus adaptés à différents groupes d'âge.",
    aDe: "Ja, das ist m\u00f6glich. Wir haben Angebote f\u00fcr verschiedene Altersgruppen.",
    aEs: "S\u00ed, es posible. Tenemos men\u00fas adaptados a diferentes grupos de edad.",
    aRu: "Да, это возможно. У нас есть меню, адаптированные для разных возрастных групп.",
    aZh: "可以。我们有针对不同年龄段的服务套餐。",
    aKo: "네, 가능합니다. 연령에 맞는 메뉴가 있습니다.",
    aPt: "Sim, é possível. Temos menus adaptados para diferentes faixas etárias.",
    aAr: "نعم، ذلك ممكن. لدينا خدمات مصممة لمختلف الفئات العمرية.",
    aHi: "हाँ, संभव है। हमारे पास विभिन्न आयु समूहों के लिए अनुकूलित मेनू हैं।",
    aTh: "ได้ เป็นไปได้ เรามีเมนูที่ปรับให้เหมาะกับกลุ่มอายุต่างๆ",
    aVi: "Có, được. Chúng tôi có các gói dịch vụ phù hợp với từng độ tuổi.",
    aId: "Ya, memungkinkan. Kami memiliki layanan yang disesuaikan untuk berbagai kelompok usia.",
    aTl: "Oo, posible. Mayroon kaming mga serbisyo na inangkop para sa iba't ibang grupo ng edad.",
    aNe: "हो, सम्भव छ। हामीसँग विभिन्न उमेर समूहका लागि अनुकूलित मेनुहरू छन्।",
    aZhTW: "可以。我們有針對不同年齡段的服務套餐。",
  },
  // Q9: 申し込み後にすることはありますか？
  {
    qJa: "申し込み後にすることはありますか？",
    qEn: "Is there anything I need to do after applying?",
    qIt: "C'è qualcosa che devo fare dopo aver inviato la richiesta?",
    qFr: "Y a-t-il quelque chose à faire après avoir soumis la demande ?",
    qDe: "Muss ich nach der Anfrage noch etwas tun?",
    qEs: "\u00bfHay algo que deba hacer despu\u00e9s de solicitar?",
    qRu: "Нужно ли что-то делать после подачи заявки?",
    qZh: "申请后需要做什么吗？",
    qKo: "신청 후에 해야 할 일이 있나요?",
    qPt: "Há algo que preciso fazer após a inscrição?",
    qAr: "هل هناك شيء يجب فعله بعد التقديم؟",
    qHi: "आवेदन के बाद कुछ करना होगा?",
    qTh: "มีอะไรต้องทำหลังสมัครหรือ?",
    qVi: "Có điều gì cần làm sau khi đăng ký không?",
    qId: "Apakah ada yang perlu dilakukan setelah mendaftar?",
    qTl: "Mayroon bang kailangang gawin pagkatapos mag-apply?",
    qNe: "दर्ता गरेपछि केही गर्नु पर्छ?",
    qZhTW: "申請後需要做什麼嗎？",
    aJa: "特別な準備は不要です。お名前と想いをお預けいただくだけで大丈夫です。",
    aEn: "No special preparation is needed. Simply entrust us with your name and feelings.",
    aIt: "Non è necessaria alcuna preparazione speciale. Affidaci semplicemente il tuo nome e i tuoi sentimenti.",
    aFr: "Aucune préparation spéciale n'est nécessaire. Confiez-nous simplement votre nom et vos sentiments.",
    aDe: "Keine besondere Vorbereitung n\u00f6tig. Vertrauen Sie uns einfach Ihren Namen und Ihre Gef\u00fchle an.",
    aEs: "No se necesita ninguna preparaci\u00f3n especial. Simplemente conf\u00ednenos su nombre y sus sentimientos.",
    aRu: "Никакой особой подготовки не требуется. Просто доверьте нам своё имя и чувства.",
    aZh: "不需要特别准备。只需将您的名字和心意交给我们即可。",
    aKo: "특별한 준비는 필요 없습니다. 이름과 마음을 맡겨주시기만 하면 됩니다.",
    aPt: "Nenhuma preparação especial é necessária. Simplesmente confie-nos seu nome e sentimentos.",
    aAr: "لا حاجة لأي تحضير خاص. فقط ثق بنا باسمك ومشاعرك.",
    aHi: "कोई विशेष तैयारी की जरूरत नहीं है। बस अपना नाम और भावनाएं हमें सौंप दें।",
    aTh: "ไม่จำเป็นต้องเตรียมการพิเศษ เพียงแค่มอบชื่อและความรู้สึกของคุณให้เรา",
    aVi: "Không cần chuẩn bị đặc biệt. Chỉ cần giao phó tên và cảm xúc của bạn cho chúng tôi.",
    aId: "Tidak diperlukan persiapan khusus. Cukup percayakan nama dan perasaan Anda kepada kami.",
    aTl: "Walang espesyal na paghahanda ang kailangan. Ipagkatiwala lamang sa amin ang iyong pangalan at damdamin.",
    aNe: "कुनै विशेष तयारी आवश्यक छैन। केवल आफ्नो नाम र भावनाहरू हामीलाई सुम्पनुहोस्।",
    aZhTW: "不需要特別準備。只需將您的名字和心意交給我們即可。",
  },
  // Q10: 周囲に知られたくありません
  {
    qJa: "周囲に知られたくありません…",
    qEn: "I don't want others to know...",
    qIt: "Non voglio che gli altri lo sappiano...",
    qFr: "Je ne veux pas que les autres le sachent...",
    qDe: "Ich m\u00f6chte nicht, dass andere es wissen...",
    qEs: "No quiero que otros lo sepan...",
    qRu: "Я не хочу, чтобы другие знали...",
    qZh: "我不想让周围的人知道……",
    qKo: "주변에 알려지고 싶지 않습니다…",
    qPt: "Não quero que outros saibam...",
    qAr: "لا أريد أن يعلم الآخرون بذلك...",
    qHi: "मैं नहीं चाहता कि दूसरों को पता चले...",
    qTh: "ฉันไม่อยากให้คนอื่นรู้...",
    qVi: "Tôi không muốn người khác biết...",
    qId: "Saya tidak ingin orang lain tahu...",
    qTl: "Ayaw kong malaman ng iba...",
    qNe: "मलाई अरूलाई थाहा हुन दिन मन छैन...",
    qZhTW: "我不想讓周圍的人知道……",
    aJa: "ご安心ください。周囲に連絡することも、SNS等に公開することも一切ありません。",
    aEn: "Please rest assured. We will never contact others or publish anything on social media.",
    aIt: "Stai tranquillo. Non contatteremo mai altri né pubblicheremo nulla sui social media.",
    aFr: "Soyez rassuré. Nous ne contacterons jamais d'autres personnes ni ne publierons quoi que ce soit sur les réseaux sociaux.",
    aDe: "Bitte seien Sie beruhigt. Wir werden niemals andere kontaktieren oder etwas in sozialen Medien ver\u00f6ffentlichen.",
    aEs: "Por favor, est\u00e9 tranquilo. Nunca contactaremos a otros ni publicaremos nada en redes sociales.",
    aRu: "Пожалуйста, будьте спокойны. Мы никогда не свяжемся с другими людьми и не опубликуем ничего в социальных сетях.",
    aZh: "请放心。我们绝不会联系他人，也不会在社交媒体上公开任何信息。",
    aKo: "안심하세요. 주변에 연락하거나 SNS 등에 공개하는 일은 절대 없습니다.",
    aPt: "Por favor, fique tranquilo. Nunca entraremos em contato com outras pessoas nem publicaremos nada nas redes sociais.",
    aAr: "يرجى الاطمئنان. لن نتواصل مع الآخرين أبداً ولن ننشر أي شيء على وسائل التواصل الاجتماعي.",
    aHi: "कृपया निश्चिंत रहें। हम कभी भी दूसरों से संपर्क नहीं करेंगे या सोशल मीडिया पर कुछ भी प्रकाशित नहीं करेंगे।",
    aTh: "กรุณาวางใจได้ เราจะไม่ติดต่อผู้อื่นหรือเผยแพร่สิ่งใดบนโซเชียลมีเดียเด็ดขาด",
    aVi: "Hãy yên tâm. Chúng tôi sẽ không liên hệ người khác hay đăng bất cứ điều gì lên mạng xã hội.",
    aId: "Mohon tenang. Kami tidak akan pernah menghubungi orang lain atau mempublikasikan apa pun di media sosial.",
    aTl: "Mangyaring mapanatag. Hindi namin kailanman makikipag-ugnayan sa iba o mag-publish ng anuman sa social media.",
    aNe: "कृपया निश्चिंत रहें। हम कहिल्यै अरूलाई सम्पर्क गर्दैनौं वा सोशल मिडियामा केही प्रकाशित गर्दैनौं।",
    aZhTW: "請放心。我們絕不會聯繫他人，也不會在社交媒體上公開任何信息。",
  },
];

export default function FaqSection() {
  const { t, i18n } = useTranslation();
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { setRef, visibleItems } = useScrollAnimationMultiple(faqsData.length, { threshold: 0.04 });
  const { ref: ctaRef, isVisible: ctaVisible } = useScrollAnimation();

  const lang = i18n.language;

  const getFaqQ = (faq: typeof faqsData[0]) => {
    if (lang === "zh-TW") return faq.qZhTW;
    if (lang === "ne") return faq.qNe;
    if (lang === "tl") return faq.qTl;
    if (lang === "id") return faq.qId;
    if (lang === "vi") return faq.qVi;
    if (lang === "th") return faq.qTh;
    if (lang === "hi") return faq.qHi;
    if (lang === "ar") return faq.qAr;
    if (lang === "pt") return faq.qPt;
    if (lang === "ko") return faq.qKo;
    if (lang === "zh") return faq.qZh;
    if (lang === "ru") return faq.qRu;
    if (lang === "es") return faq.qEs;
    if (lang === "de") return faq.qDe;
    if (lang === "fr") return faq.qFr;
    if (lang === "it") return faq.qIt;
    if (lang === "en") return faq.qEn;
    return faq.qJa;
  };

  const getFaqA = (faq: typeof faqsData[0]) => {
    if (lang === "zh-TW") return faq.aZhTW;
    if (lang === "ne") return faq.aNe;
    if (lang === "tl") return faq.aTl;
    if (lang === "id") return faq.aId;
    if (lang === "vi") return faq.aVi;
    if (lang === "th") return faq.aTh;
    if (lang === "hi") return faq.aHi;
    if (lang === "ar") return faq.aAr;
    if (lang === "pt") return faq.aPt;
    if (lang === "ko") return faq.aKo;
    if (lang === "zh") return faq.aZh;
    if (lang === "ru") return faq.aRu;
    if (lang === "es") return faq.aEs;
    if (lang === "de") return faq.aDe;
    if (lang === "fr") return faq.aFr;
    if (lang === "it") return faq.aIt;
    if (lang === "en") return faq.aEn;
    return faq.aJa;
  };

  const handleNav = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const toggle = (i: number) => {
    setOpenIndex(openIndex === i ? null : i);
  };

  return (
    <section id="faq" className="py-16 sm:py-24 bg-[#e8f9f8]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <div
          ref={headerRef as React.RefObject<HTMLDivElement>}
          className={`text-center mb-10 sm:mb-14 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-800 mb-5" style={{ fontFamily: "'Noto Serif JP', serif" }}>
            {t("faq_heading")}
          </h2>
        </div>

        <div className="space-y-3" role="list" aria-label={t("faq_heading")}>
          {faqsData.map((faq, i) => (
            <div
              key={i}
              ref={setRef(i) as unknown as React.RefObject<HTMLDivElement>}
              className={`bg-white/80 rounded-2xl border transition-all duration-500 ease-out overflow-hidden ${
                openIndex === i ? "border-[#0ABAB5]/40" : "border-[#0ABAB5]/10"
              } ${visibleItems[i] ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}
              style={{ transitionDelay: visibleItems[i] ? `${Math.min(i * 40, 400)}ms` : "0ms" }}
              role="listitem"
            >
              <button
                onClick={() => toggle(i)}
                aria-expanded={openIndex === i}
                aria-controls={`faq-answer-${i}`}
                id={`faq-question-${i}`}
                className="w-full flex items-start gap-4 px-6 py-5 text-left cursor-pointer group"
              >
                <span className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full bg-[#e8f9f8] text-[#0ABAB5] text-xs font-bold mt-0.5" aria-hidden="true">Q</span>
                <span className="flex-1 text-sm font-semibold text-gray-800 leading-relaxed group-hover:text-[#0ABAB5] transition-colors duration-200" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                  {getFaqQ(faq)}
                </span>
                <span className="flex-shrink-0 w-6 h-6 flex items-center justify-center mt-0.5" aria-hidden="true">
                  <i className={`text-gray-400 text-base transition-transform duration-300 ${openIndex === i ? "ri-subtract-line rotate-0" : "ri-add-line"}`} />
                </span>
              </button>
              <div
                id={`faq-answer-${i}`}
                role="region"
                aria-labelledby={`faq-question-${i}`}
                className={`transition-all duration-300 ease-in-out overflow-hidden ${openIndex === i ? "max-h-96 opacity-100" : "max-h-0 opacity-0"}`}
              >
                <div className="flex items-start gap-4 px-6 pb-5">
                  <span className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-full bg-[#0ABAB5] text-white text-xs font-bold mt-0.5" aria-hidden="true">A</span>
                  <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">{getFaqA(faq)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div
          ref={ctaRef as React.RefObject<HTMLDivElement>}
          className={`mt-14 bg-white/80 rounded-2xl border border-[#0ABAB5]/10 px-8 py-10 text-center transition-all duration-700 ease-out ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
          style={{ transitionDelay: ctaVisible ? "100ms" : "0ms" }}
        >
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#e8f9f8] mx-auto mb-4">
            <i className="ri-question-line text-[#0ABAB5] text-xl" />
          </div>
          <h4 className="text-xl font-bold text-gray-800 mb-2" style={{ fontFamily: "'Noto Serif JP', serif" }}>
            {t("faq_cta_title")}
          </h4>
          <p className="text-gray-500 text-sm leading-relaxed mb-6">
            {t("faq_cta_desc").split("\n").map((line, i, arr) => (
              <span key={i}>{line}{i < arr.length - 1 && <br />}</span>
            ))}
          </p>
          <button
            onClick={() => handleNav("#contact")}
            className="inline-flex items-center gap-2 bg-[#0ABAB5] text-white text-sm font-semibold px-8 py-3.5 rounded-full hover:bg-[#089e99] transition-colors duration-200 whitespace-nowrap cursor-pointer"
          >
            {t("faq_cta_btn")}
            <i className="ri-arrow-right-line" />
          </button>
        </div>
      </div>
    </section>
  );
}
